import { ReactNode, useRef, useEffect } from 'react';

import { BadgeCircle, InputBox, type InputBoxProps } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useKeyboardDetection } from '@backrs_ui/grow-ui/src/hooks/useKeyboardDetection';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { ActiveTool } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { transcribeAudio } from '@backrs_ui/grow-ui/src/redux/coachLubav/transcribeAudio';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, Typography, SxProps, Theme, useMediaQuery } from '@mui/material';

import { HEADER_HEIGHT } from './Header';
import { TypingIndicator } from './TypingIndicator';

const CHAT_BOX_STRINGS = appStrings.modules.chatBox;

type ChatBoxContainerProps = Readonly<{
  children: ReactNode;
  isTyping: boolean;
  inputProps: InputBoxProps;
  suggestedPrompts?: ReactNode;
  showSuggestedPrompts: boolean;

  // if true, the suggested prompts will be sticky to the bottom of the chatbox
  stickySuggestedPrompts: boolean;

  // mobile toggle button for switching between chat and info views
  scrollKey?: string;

  // header buttons -- header is only visible if a button is provided
  headerButtons?: ReactNode;

  // active tool indicators
  activeTools?: ActiveTool[];

  readOnly?: boolean;
}>;

export const ChatBoxContainer = ({
  children,
  isTyping,
  inputProps,
  suggestedPrompts,
  showSuggestedPrompts,
  stickySuggestedPrompts,
  scrollKey,
  headerButtons,
  activeTools = [],
  readOnly = false,
}: ChatBoxContainerProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const userScrolledAwayRef = useRef(false);
  const isTablet = useMediaQuery('(max-width: 900px)');
  const { keyboardHeight } = useKeyboardDetection();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    let lastScrollTop = el.scrollTop;

    const handleScroll = () => {
      const scrollDelta = el.scrollTop - lastScrollTop;
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;

      if (scrollDelta < -5) {
        userScrolledAwayRef.current = true;
      }

      if (isAtBottom) {
        userScrolledAwayRef.current = false;
      }

      lastScrollTop = el.scrollTop;
    };

    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;

    if (!el || userScrolledAwayRef.current) return;

    el.scrollTop = el.scrollHeight;

    const delay = setTimeout(() => {
      if (!userScrolledAwayRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    }, 100);

    return () => clearTimeout(delay);
  }, [scrollKey, isTyping, keyboardHeight]);

  return (
    <Box sx={containerSx(isTablet)}>
      {!isTablet && headerButtons && (
        <Box sx={headerWrapperSx}>
          <Box sx={headerContentSx}>
            <Box sx={coachLubavSx}>
              <BadgeCircle size="lg" />
              <Typography variant="text-lg-semibold">{CHAT_BOX_STRINGS.askLubav}</Typography>
            </Box>
            <Box sx={headerButtonsSx}>{headerButtons}</Box>
          </Box>
        </Box>
      )}
      <Box sx={contentSx}>
        <Box ref={scrollRef} sx={chatBoxSx}>
          {children}
          {isTyping && <TypingIndicator activeTools={activeTools} />}
          {showSuggestedPrompts && !stickySuggestedPrompts && suggestedPrompts}
        </Box>
        {stickySuggestedPrompts && showSuggestedPrompts && (
          <Box sx={stickySuggestedPromptSx}>{suggestedPrompts}</Box>
        )}
        <Box sx={inputBoxSx}>
          <InputBox
            {...inputProps}
            disabled={readOnly || inputProps.disabled}
            onTranscribe={inputProps.onTranscribe ?? transcribeAudio}
            onRecordingError={
              inputProps.onRecordingError ??
              ((message) => dispatch(showSnackbar({ message, severity: 'error' })))
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

const headerWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  // invisible hover area to trigger header expansion
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '54px',
    pointerEvents: 'auto',
  },
  '&:hover > div': {
    opacity: 1,
    maxHeight: '200px',
    paddingTop: backrs.spacing['spacing-2xl'],
    paddingBottom: backrs.spacing['spacing-2xl'],
    borderBottomColor: backrs.colors['Beige/950'],
  },
});

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: backrs.colors['Colors/Base/White'],
  paddingLeft: backrs.spacing['spacing-2xl'],
  paddingRight: backrs.spacing['spacing-2xl'],
  paddingTop: 0,
  paddingBottom: 0,
  maxHeight: 0,
  overflow: 'hidden',
  opacity: 0,
  borderBottom: '1px solid transparent',
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  zIndex: 2,
});

const containerSx =
  (isTablet: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexGrow: 1,
    height: '100%',
    boxSizing: 'border-box',
    backgroundColor: backrs.colors['Colors/Base/White'],
    overflow: 'hidden',
    ...(!isTablet && {
      position: 'relative',
      height: `calc(100vh - ${HEADER_HEIGHT}px - ${backrs.spacing['spacing-md']})`,
      border: `1px solid ${backrs.colors['Beige/950']}`,
      borderRadius: backrs.radius['radius-xl'],
      boxShadow: backrs.shadows['shadow-lg'],
    }),
  });

const contentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  height: 0,
  flex: 1,
  boxSizing: 'border-box',
  overflow: 'hidden',
  ml: 0,
};

const chatBoxSx: SxProps<Theme> = ({ backrs }) => ({
  flexGrow: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  padding: {
    xs: backrs.spacing['spacing-xl'],
    md: backrs.spacing['spacing-3xl'],
  },
  pl: {
    xs: backrs.spacing['spacing-xl'],
    md: backrs.spacing['spacing-xl'],
  },
});

const stickySuggestedPromptSx: SxProps<Theme> = ({ backrs }) => ({
  pt: backrs.spacing['spacing-lg'],
  px: backrs.spacing['spacing-3xl'],
});

const inputBoxSx: SxProps<Theme> = ({ backrs }) => ({
  px: {
    xs: backrs.spacing['spacing-md'],
    md: backrs.spacing['spacing-3xl'],
  },
  pr: {
    xs: backrs.spacing['spacing-lg'],
    md: backrs.spacing['spacing-3xl'],
  },
  paddingBottom: {
    xs: backrs.spacing['spacing-xl'],
    md: backrs.spacing['spacing-3xl'],
  },
  paddingTop: 0,
});

const coachLubavSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
});

const headerButtonsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
});
